import { globalConfig } from 'core/config';
import { analytics } from 'core/analytics';
import { getOperationName } from '@apollo/client/utilities';

export function isNotFound(userErr) {
  if (userErr.code === 'NOTFOUND') {
    return true;
  }
  return false;
}

// Used for backend go error codes only
export const ErrorCode = {
  VALIDATION: 'VALIDATION',
  AUTHENTICATION: 'AUTHENTICATION',
  AUTHORISATION: 'AUTHORISATION',
  NOTFOUND: 'NOTFOUND',
  REDIRECT: 'REDIRECT',
  USER_MISMATCH: 'USER_MISMATCH',
  USER_NOT_CONFIRMED: 'USER_NOT_CONFIRMED',
  PROJECT_PAYMENT_OVERDUE: 'PROJECT_PAYMENT_OVERDUE',
  BILLING_CARD_DECLINED: 'BILLING_CARD_DECLINED',
  BILLING_ERROR: 'BILLING_ERROR',
  INVITE_ERROR: 'INVITE_ERROR',
  UNHANDLED_ERROR: 'UNHANDLED_ERROR',
  NO_BOT: 'NO_BOT',
  BAD_REQUEST: 'BAD_REQUEST',
  INVALID_TOKEN: 'INVALID_TOKEN',
  UPLOAD_INCOMPLETE: 'UPLOAD_INCOMPLETE',
  LOCKED_FILES: 'LOCKED_FILES',
};

// Python Backend Error Handling

export const reportRequestError = (err, queryOrMut) => {
  if (window.location.hostname !== 'wikifactory.com') {
    console.warn(queryOrMut);
    if (queryOrMut.queryStack) {
      console.warn(queryOrMut.queryStack);
    }
  }

  const scopingFunction = scope => {
    try {
      if (queryOrMut.appManager && queryOrMut.appManager.constructor) {
        // - making these tags instead of extras, so we can search by appManager
        // and registryName
        // - also made them separate things, cause registryName is ofter just
        // AppManager, but the constructor.name is interesting
        scope.setTag('appManager', queryOrMut.appManager.constructor.name);
        if (queryOrMut.appManager.constructor?.registryName) {
          scope.setTag(
            'registryName',
            queryOrMut.appManager.constructor?.registryName,
          );
        }
      }
      if (queryOrMut.observableQuery && queryOrMut.observableQuery.variables) {
        scope.setExtra(
          'variables',
          JSON.stringify(queryOrMut.observableQuery.variables),
        );
      }
    } catch (e) {
      console.error('An error ocurred while handling an error!');
    }
  };

  // - setting the stack to undefined forces sentry to group the error by message,
  // the stack is almost always the same (some apollo stuff and pretty much useless),
  // so I am willing to sacrifice it here
  // - even if we get a different stack, I feel like these are most of the time useless
  const error = err;
  error.stack = undefined;

  analytics.logError(error, scopingFunction);
};

export const handleGraphqlErrors = (queryOrMut, err) => {
  /* For authentication errors we really need to do something different depending on whether the user is loggedIn (has jwt)!
  Backend go doesn't really know if the user is logged out or not, just whether the user has a valid JWT.

  If the user receives an authentication error but is logged in we should return a 404 or tell them they don't have access.
 */

  let operationName = '';
  try {
    operationName = getOperationName(queryOrMut.constructor.conf?.GQL);
    // eslint-disable-next-line no-param-reassign
    if (err.message) err.message = `${operationName}: ${err.message}`;
  } catch (utilErr) {
    console.error(utilErr);
  }

  const userErrs = [];
  // if there are no graphqlErrors, then it's a network error
  if (!err?.graphQLErrors) {
    return [err, []];
  }

  err.graphQLErrors.forEach(graphqlErrData => {
    const graphqlErr = Object.assign(new Error(), graphqlErrData);
    if (operationName)
      graphqlErr.message = `${operationName}: ${graphqlErr.message}`;

    if (graphqlErr.extensions?.code !== undefined) {
      const { code: errorCode } = graphqlErr.extensions;
      if (errorCode === ErrorCode.AUTHENTICATION) {
        if (globalConfig.onAuthenticationError) {
          globalConfig.onAuthenticationError(graphqlErr);
        }
        userErrs.push(graphqlErr);
      } else if (errorCode === ErrorCode.AUTHORISATION) {
        queryOrMut.notFound = true; // could be moved to onAuthorisationError
        if (globalConfig.onAuthorisationError) {
          globalConfig.onAuthorisationError(graphqlErr);
        }
        userErrs.push(graphqlErr);
      } else if (errorCode === ErrorCode.VALIDATION) {
        // deal with validationErrors and any other user errors, by adding them to the userErrors array
        userErrs.push(graphqlErr);
      } else if (errorCode === ErrorCode.BAD_REQUEST) {
        // queryOrMut.notFound = true;
        // if (globalConfig?.onNotFoundError) {
        //   globalConfig.onNotFoundError(graphqlErr);
        // }
        userErrs.push(graphqlErr);
      } else if (errorCode === ErrorCode.UNHANDLED_ERROR) {
        // do nothing this is effectively a sysErr
      } else if (errorCode === ErrorCode.USER_MISMATCH) {
        globalConfig.onUserMismatchError(graphqlErr);
        userErrs.push(graphqlErr);
      } else if (errorCode === ErrorCode.REDIRECT) {
        // In coreConf, neither onAuthorisationError nor onRedirectError are defined
        userErrs.push(graphqlErr);
        if (globalConfig.onAuthorisationError) {
          globalConfig.onRedirectError(graphqlErr);
        }
        userErrs.push(graphqlErr);
      } else if (errorCode === ErrorCode.NO_BOT) {
        if (globalConfig.onNoBot) {
          globalConfig.onNoBot(graphqlErr);
        }
        userErrs.push(graphqlErr);
      } else if (errorCode === ErrorCode.NOTFOUND) {
        queryOrMut.notFound = true;
        if (globalConfig.onNotFoundError) {
          globalConfig.onNotFoundError(graphqlErr);
        }
        userErrs.push(graphqlErr);
      } else if (errorCode === ErrorCode.USER_NOT_CONFIRMED) {
        if (globalConfig.onNotConfirmedError) {
          globalConfig.onNotConfirmedError(graphqlErr);
        }
        userErrs.push(graphqlErr);
      } else if (errorCode === ErrorCode.PROJECT_PAYMENT_OVERDUE) {
        if (globalConfig.onPaymentOverdueError) {
          globalConfig.onPaymentOverdueError(graphqlErr);
        }
        userErrs.push(graphqlErr);
      } else if (errorCode === ErrorCode.BILLING_CARD_DECLINED) {
        userErrs.push(graphqlErr);
      } else if (errorCode === ErrorCode.BILLING_ERROR) {
        userErrs.push(graphqlErr);
      } else if (errorCode === ErrorCode.INVITE_ERROR) {
        userErrs.push(graphqlErr);
      } else if (errorCode === ErrorCode.INVALID_TOKEN) {
        userErrs.push(graphqlErr);
      } else if (errorCode === ErrorCode.UPLOAD_INCOMPLETE) {
        userErrs.push(graphqlErr);
      } else if (errorCode === ErrorCode.LOCKED_FILES) {
        userErrs.push(graphqlErr);
      }
    }
  });
  // if there are no specific Wikifactory errors codes found then this is a generic sys error
  if (userErrs.length === 0) {
    return [err, []];
  }
  return [null, userErrs];
};
